import { apiService, ApiResponse } from './apiService'
import type {
  FieldLabelsResponse,
  UpdateLabelParams,
  FieldConfiguration,
  BulkUpdateResponse
} from '../types/labels.types'

/**
 * Label Service
 * Handles field label configuration API calls
 */
export class LabelService {
  /**
   * Get field labels for a specific table context
   * @param context - Table context name (e.g., 'offerings', 'customers', 'costs')
   * @returns Field labels with customization and visibility flags
   * @throws Error if the fetch fails
   */
  async getLabels(context: string): Promise<ApiResponse<FieldLabelsResponse>> {
    const response = await apiService.get<FieldLabelsResponse>(
      `/field_configurations/labels/${context}`
    )

    if (!response.success) {
      const errorMsg = response.errors?.[0]?.message || 'Unknown error'
      console.error('Failed to fetch labels for context:', context, response.errors)
      throw new Error(errorMsg)
    }

    return response
  }

  /**
   * Update or create a field label configuration
   * @param params - Label update parameters
   * @returns Updated field configuration
   * @throws Error if the update/create fails
   */
  async updateLabel(params: UpdateLabelParams): Promise<ApiResponse<FieldConfiguration>> {
    const payload = {
      context: params.context,
      fieldName: params.fieldName,
      configType: 'label',
      value: {
        label: params.label,
        visible: params.visible
      }
    }

    // Use PUT if UID exists (update), POST if not (create)
    // Include version in body for optimistic locking on updates
    const response = params.uid
      ? await apiService.put<FieldConfiguration>(
          `/field_configurations/${params.uid}`,
          params.version !== undefined ? { ...payload, version: params.version } : payload
        )
      : await apiService.post<FieldConfiguration>('/field_configurations', payload)

    if (!response.success) {
      const errorMsg = response.errors?.[0]?.message || 'Unknown error'
      console.error(
        'Failed to update label for field:',
        params.fieldName,
        response.errors
      )
      throw new Error(errorMsg)
    }

    return response
  }

  /**
   * Bulk update field label configurations
   * @param items - Array of field configurations to update (must have UIDs)
   * @returns Bulk update response with per-item results
   * @throws Error if the bulk update fails completely
   */
  async bulkUpdate(items: any[]): Promise<ApiResponse<BulkUpdateResponse>> {
    console.log('🔍 bulkUpdate - Request payload:', JSON.stringify({ items }, null, 2))

    const response = await apiService.bulkOperation<BulkUpdateResponse>(
      '/field_configurations',
      'update',
      items
    )

    console.log('🔍 bulkUpdate - Response:', JSON.stringify(response, null, 2))

    if (!response.success) {
      const errorMsg = response.errors?.[0]?.message || 'Unknown error'
      throw new Error(errorMsg)
    }

    return response
  }

  /**
   * Bulk upsert field label configurations (create or update)
   * Handles items with or without UIDs - creates new records for items without UIDs
   * @param items - Array of field configurations to upsert
   * @returns Bulk upsert response with per-item results
   * @throws Error if the bulk upsert fails completely
   */
  async bulkUpsert(items: any[]): Promise<ApiResponse<BulkUpdateResponse>> {
    console.log('🔍 bulkUpsert - Request payload:', JSON.stringify({ items }, null, 2))

    const response = await apiService.bulkOperation<BulkUpdateResponse>(
      '/field_configurations',
      'upsert',
      items
    )

    console.log('🔍 bulkUpsert - Response:', JSON.stringify(response, null, 2))

    if (!response.success) {
      const errorMsg = response.errors?.[0]?.message || 'Unknown error'
      throw new Error(errorMsg)
    }

    return response
  }

  /**
   * Delete a field label configuration (reset to default)
   * @param uid - Field configuration UID (from labels response)
   * @returns Deletion result
   * @throws Error if the deletion fails
   */
  async deleteLabel(uid: string): Promise<ApiResponse<void>> {
    const response = await apiService.delete<void>(
      `/field_configurations/${uid}`
    )

    if (!response.success) {
      const errorMsg = response.errors?.[0]?.message || 'Unknown error'
      console.error(
        'Failed to delete label configuration:',
        uid,
        response.errors
      )
      throw new Error(errorMsg)
    }

    return response
  }

  /**
   * Bulk delete field label configurations (reset to defaults)
   * @param uids - Array of field configuration UIDs to delete
   * @returns Bulk delete response with per-item results
   * @throws Error if the bulk delete fails completely
   */
  async bulkDelete(uids: string[]): Promise<ApiResponse<BulkUpdateResponse>> {
    console.log('🔍 bulkDelete - Request payload:', JSON.stringify({ uids }, null, 2))

    // Note: delete endpoint expects 'uids' parameter, not 'items'
    const response = await apiService.post<BulkUpdateResponse>(
      '/field_configurations/bulk/delete',
      { uids }
    )

    console.log('🔍 bulkDelete - Response:', JSON.stringify(response, null, 2))

    if (!response.success) {
      const errorMsg = response.errors?.[0]?.message || 'Unknown error'
      throw new Error(errorMsg)
    }

    return response
  }
}

// Export singleton instance
export const labelService = new LabelService()
export default labelService
